// src/pages/HomePage.jsx
import React from 'react';
import {Link} from 'react-router-dom';
import {useAuth} from '../contexts/AuthContext';

export default function HomePage() {
    const {currentUser} = useAuth();

    return (
        <div className="min-h-screen bg-gradient-to-b from-indigo-800 to-purple-900 text-white">
            {/* Header avec Logo */}
            <header className="pt-6 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto flex justify-center">
                    {/* Logo BibleQuiz SVG */}
                    <div className="w-40 h-40">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 500 500">
                            {/* Fond circulaire */}
                            <circle cx="250" cy="250" r="240" fill="#f8f4e5"/>
                            <circle cx="250" cy="250" r="210" fill="#3b5998"/>

                            {/* Livre ouvert (Bible) */}
                            <path
                                d="M130,190 C130,180 140,170 160,170 L340,170 C360,170 370,180 370,190 L370,330 C370,340 360,350 340,350 L160,350 C140,350 130,340 130,330 Z"
                                fill="#f8f4e5"/>

                            {/* Pages de gauche */}
                            <path
                                d="M130,190 C130,180 140,170 160,170 L240,170 L240,350 L160,350 C140,350 130,340 130,330 Z"
                                fill="#ffffff"/>

                            {/* Effets d'ombre pour la page */}
                            <path d="M245,170 L245,350 L242,350 L242,170 Z" fill="#d0cebf"/>

                            {/* Lignes décoratives (texte symbolique) sur la page gauche */}
                            <path d="M150,210 L220,210" stroke="#c0b8a0" strokeWidth="2"/>
                            <path d="M150,230 L220,230" stroke="#c0b8a0" strokeWidth="2"/>
                            <path d="M150,250 L220,250" stroke="#c0b8a0" strokeWidth="2"/>
                            <path d="M150,270 L220,270" stroke="#c0b8a0" strokeWidth="2"/>
                            <path d="M150,290 L220,290" stroke="#c0b8a0" strokeWidth="2"/>
                            <path d="M150,310 L220,310" stroke="#c0b8a0" strokeWidth="2"/>

                            {/* Point d'interrogation stylisé sur la page droite */}
                            <path d="M290,300 Q290,270 310,260 Q330,250 330,235 Q330,215 305,215 Q285,215 280,235"
                                  fill="none" stroke="#f1c232" strokeWidth="14" strokeLinecap="round"/>
                            <circle cx="290" cy="320" r="8" fill="#f1c232"/>

                            {/* Croix en filigrane */}
                            <path d="M250,170 L250,350" stroke="#d3d9e9" strokeWidth="6" opacity="0.5"/>
                            <path d="M180,250 L320,250" stroke="#d3d9e9" strokeWidth="6" opacity="0.5"/>

                            {/* Halo de lumière */}
                            <circle cx="250" cy="250" r="180" fill="none" stroke="#e6c545" strokeWidth="2"
                                    opacity="0.7"/>
                            <circle cx="250" cy="250" r="195" fill="none" stroke="#e6c545" strokeWidth="1"
                                    opacity="0.5"/>

                            {/* Texte "BIBLE QUIZ" en bas */}
                            <path id="textArc" d="M80,250 A170,170 0 0,0 420,250" fill="none"/>
                            <text fontFamily="Arial, sans-serif" fontSize="32" fontWeight="bold" fill="#ffffff">
                                <textPath href="#textArc" startOffset="32%">BIBLE QUIZ</textPath>
                            </text>

                            {/* Étoile décorative plus grande */}
                            <path
                                d="M250,65 L262,95 L295,95 L270,115 L280,145 L250,125 L220,145 L230,115 L205,95 L238,95 Z"
                                fill="#f1c232"/>
                        </svg>
                    </div>
                </div>
            </header>

            {/* Hero Section principale */}
            <div className="relative overflow-hidden pt-6">
                <div className="max-w-7xl mx-auto">
                    <div className="relative z-10 pb-8 sm:pb-16 md:pb-20 lg:w-full lg:pb-28 xl:pb-32">
                        <main className="mt-4 mx-auto max-w-7xl px-4 sm:mt-6 sm:px-6 lg:mt-8">
                            <div className="text-center">
                                <h1 className="text-4xl tracking-tight font-extrabold text-white sm:text-5xl md:text-6xl">
                                    <span className="block">Testez votre connaissance</span>
                                    <span className="block text-yellow-400">des Écritures Saintes</span>
                                </h1>
                                <p className="mt-3 max-w-md mx-auto text-base text-indigo-100 sm:text-lg md:mt-5 md:text-xl md:max-w-3xl">
                                    Découvrez BibleQuiz, l'application qui vous permet d'explorer les Écritures à
                                    travers des quiz thématiques variés, des défis chronométrés et un suivi détaillé de
                                    votre progression.
                                </p>
                                <div className="mt-10 sm:flex sm:justify-center">
                                    {currentUser ? (
                                        <div className="rounded-md shadow">
                                            <Link
                                                to="/dashboard"
                                                className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-indigo-700 bg-white hover:bg-gray-50 md:py-4 md:text-lg md:px-10 transition transform hover:scale-105 duration-200"
                                            >
                                                Accéder à mon espace
                                            </Link>
                                        </div>
                                    ) : (
                                        <div
                                            className="space-y-4 sm:space-y-0 sm:mx-auto sm:inline-grid sm:grid-cols-2 sm:gap-5">
                                            <div className="rounded-md shadow">
                                                <Link
                                                    to="/signup"
                                                    className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-indigo-700 bg-white hover:bg-gray-50 md:py-4 md:text-lg md:px-10 transition transform hover:scale-105 duration-200"
                                                >
                                                    Inscription
                                                </Link>
                                            </div>
                                            <div className="rounded-md shadow">
                                                <Link
                                                    to="/login"
                                                    className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-yellow-500 hover:bg-yellow-600 md:py-4 md:text-lg md:px-10 transition transform hover:scale-105 duration-200"
                                                >
                                                    Connexion
                                                </Link>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </main>
                    </div>
                </div>
            </div>

            {/* Features Section avec grille de fonctionnalités */}
            <div className="py-12 bg-indigo-900 bg-opacity-30">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="lg:text-center">
                        <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-white sm:text-4xl">
                            Enrichissez votre chemin spirituel
                        </p>
                        <p className="mt-4 max-w-2xl text-xl text-indigo-200 lg:mx-auto">
                            "Ta parole est une lampe à mes pieds, et une lumière sur mon sentier." - Psaume 119:105
                        </p>
                    </div>

                    <div className="mt-10">
                        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
                            <div
                                className="flex flex-col items-center p-6 bg-white bg-opacity-10 rounded-lg shadow-lg backdrop-filter backdrop-blur-sm transform transition duration-500 hover:scale-105">
                                <div
                                    className="flex items-center justify-center h-16 w-16 rounded-full bg-yellow-500 text-white text-2xl font-bold">
                                    📚
                                </div>
                                <h3 className="mt-4 text-lg font-medium text-white">Quiz thématiques</h3>
                                <p className="mt-2 text-base text-indigo-200 text-center">
                                    Explorez 12 thèmes bibliques différents, des personnages de l'Ancien Testament aux
                                    paraboles de Jésus, en passant par les symboles et typologies bibliques.
                                </p>
                            </div>

                            <div
                                className="flex flex-col items-center p-6 bg-white bg-opacity-10 rounded-lg shadow-lg backdrop-filter backdrop-blur-sm transform transition duration-500 hover:scale-105">
                                <div
                                    className="flex items-center justify-center h-16 w-16 rounded-full bg-yellow-500 text-white text-2xl font-bold">
                                    ⏱️
                                </div>
                                <h3 className="mt-4 text-lg font-medium text-white">Mode chronométré</h3>
                                <p className="mt-2 text-base text-indigo-200 text-center">
                                    Testez vos connaissances sous pression avec notre mode chronométré, où chaque
                                    seconde compte pour maximiser votre score.
                                </p>
                            </div>

                            <div
                                className="flex flex-col items-center p-6 bg-white bg-opacity-10 rounded-lg shadow-lg backdrop-filter backdrop-blur-sm transform transition duration-500 hover:scale-105">
                                <div
                                    className="flex items-center justify-center h-16 w-16 rounded-full bg-yellow-500 text-white text-2xl font-bold">
                                    🎮
                                </div>
                                <h3 className="mt-4 text-lg font-medium text-white">Mode entraînement</h3>
                                <p className="mt-2 text-base text-indigo-200 text-center">
                                    Créez des sessions d'entraînement personnalisées pour renforcer vos connaissances à
                                    votre rythme.
                                </p>
                            </div>

                            <div
                                className="flex flex-col items-center p-6 bg-white bg-opacity-10 rounded-lg shadow-lg backdrop-filter backdrop-blur-sm transform transition duration-500 hover:scale-105">
                                <div
                                    className="flex items-center justify-center h-16 w-16 rounded-full bg-yellow-500 text-white text-2xl font-bold">
                                    📊
                                </div>
                                <h3 className="mt-4 text-lg font-medium text-white">Suivi de progression</h3>
                                <p className="mt-2 text-base text-indigo-200 text-center">
                                    Visualisez votre progression et identifiez vos points forts et vos opportunités
                                    d'amélioration.
                                </p>
                            </div>

                            <div
                                className="flex flex-col items-center p-6 bg-white bg-opacity-10 rounded-lg shadow-lg backdrop-filter backdrop-blur-sm transform transition duration-500 hover:scale-105">
                                <div
                                    className="flex items-center justify-center h-16 w-16 rounded-full bg-yellow-500 text-white text-2xl font-bold">
                                    🏆
                                </div>
                                <h3 className="mt-4 text-lg font-medium text-white">Classements</h3>
                                <p className="mt-2 text-base text-indigo-200 text-center">
                                    Comparez vos performances avec d'autres joueurs et atteignez le sommet des
                                    classements pour chaque quiz thématique.
                                </p>
                            </div>

                            <div
                                className="flex flex-col items-center p-6 bg-white bg-opacity-10 rounded-lg shadow-lg backdrop-filter backdrop-blur-sm transform transition duration-500 hover:scale-105">
                                <div
                                    className="flex items-center justify-center h-16 w-16 rounded-full bg-yellow-500 text-white text-2xl font-bold">
                                    📱
                                </div>
                                <h3 className="mt-4 text-lg font-medium text-white">Multi-plateforme</h3>
                                <p className="mt-2 text-base text-indigo-200 text-center">
                                    Jouez à BibleQuiz sur tous vos appareils, ordinateur, tablette ou smartphone, avec
                                    une expérience optimisée.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Section Catégories de Quiz */}
            <div className="py-12 bg-indigo-950 bg-opacity-40">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h2 className="text-3xl font-extrabold text-center text-white mb-12">
                        Nos Quiz Thématiques
                    </h2>
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        <div
                            className="bg-white bg-opacity-10 rounded-lg p-6 backdrop-filter backdrop-blur-sm transform transition duration-500 hover:scale-105">
                            <h3 className="text-xl font-semibold text-yellow-400">Les Livres de la Bible</h3>
                            <span
                                className="inline-block px-2 py-1 mt-2 text-xs font-semibold rounded-full bg-green-100 text-green-800">Facile</span>
                            <p className="mt-3 text-indigo-100">Testez vos connaissances sur l'ordre et le contenu des
                                livres bibliques.</p>
                        </div>
                        <div
                            className="bg-white bg-opacity-10 rounded-lg p-6 backdrop-filter backdrop-blur-sm transform transition duration-500 hover:scale-105">
                            <h3 className="text-xl font-semibold text-yellow-400">Personnages de l'Ancien Testament</h3>
                            <span
                                className="inline-block px-2 py-1 mt-2 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">Moyen</span>
                            <p className="mt-3 text-indigo-100">Découvrez les grands personnages de l'Ancien Testament
                                et leurs histoires.</p>
                        </div>
                        <div
                            className="bg-white bg-opacity-10 rounded-lg p-6 backdrop-filter backdrop-blur-sm transform transition duration-500 hover:scale-105">
                            <h3 className="text-xl font-semibold text-yellow-400">La vie de Jésus</h3>
                            <span
                                className="inline-block px-2 py-1 mt-2 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">Moyen</span>
                            <p className="mt-3 text-indigo-100">Quiz sur la naissance, le ministère, les miracles et les
                                enseignements de Jésus.</p>
                        </div>
                        <div
                            className="bg-white bg-opacity-10 rounded-lg p-6 backdrop-filter backdrop-blur-sm transform transition duration-500 hover:scale-105">
                            <h3 className="text-xl font-semibold text-yellow-400">Les Épîtres de Paul</h3>
                            <span
                                className="inline-block px-2 py-1 mt-2 text-xs font-semibold rounded-full bg-red-100 text-red-800">Difficile</span>
                            <p className="mt-3 text-indigo-100">Testez vos connaissances sur les lettres de l'apôtre
                                Paul.</p>
                        </div>
                        <div
                            className="bg-white bg-opacity-10 rounded-lg p-6 backdrop-filter backdrop-blur-sm transform transition duration-500 hover:scale-105">
                            <h3 className="text-xl font-semibold text-yellow-400">Prophéties et Prophètes</h3>
                            <span
                                className="inline-block px-2 py-1 mt-2 text-xs font-semibold rounded-full bg-red-100 text-red-800">Difficile</span>
                            <p className="mt-3 text-indigo-100">Découvrez les prophètes et leurs messages dans la
                                Bible.</p>
                        </div>
                        <div
                            className="bg-white bg-opacity-10 rounded-lg p-6 backdrop-filter backdrop-blur-sm transform transition duration-500 hover:scale-105">
                            <h3 className="text-xl font-semibold text-yellow-400">Les Femmes de la Bible</h3>
                            <span
                                className="inline-block px-2 py-1 mt-2 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">Moyen</span>
                            <p className="mt-3 text-indigo-100">Découvrez les personnages féminins importants et leur
                                impact dans l'histoire biblique.</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Section Témoignages */}
            <div className="py-16 bg-indigo-800 bg-opacity-30">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h2 className="text-3xl font-extrabold text-center text-white mb-12">
                        Ce que disent nos utilisateurs
                    </h2>
                    <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
                        <div
                            className="bg-white bg-opacity-10 rounded-lg p-6 relative backdrop-filter backdrop-blur-sm transform transition duration-500 hover:scale-105">
                            <div className="absolute -top-3 -left-3 text-6xl text-yellow-400 opacity-50">"</div>
                            <p className="text-indigo-100 italic relative z-10 mt-4">BibleQuiz a transformé notre étude
                                biblique en famille. Les enfants adorent le format ludique et nous apprenons tous
                                ensemble!</p>
                            <p className="mt-4 font-medium text-yellow-300">Marie L., enseignante</p>
                        </div>
                        <div
                            className="bg-white bg-opacity-10 rounded-lg p-6 relative backdrop-filter backdrop-blur-sm transform transition duration-500 hover:scale-105">
                            <div className="absolute -top-3 -left-3 text-6xl text-yellow-400 opacity-50">"</div>
                            <p className="text-indigo-100 italic relative z-10 mt-4">En tant que pasteur, j'utilise
                                BibleQuiz avec les jeunes de mon église. C'est un outil pédagogique formidable qui les
                                engage vraiment.</p>
                            <p className="mt-4 font-medium text-yellow-300">Pierre D., pasteur</p>
                        </div>
                        <div
                            className="bg-white bg-opacity-10 rounded-lg p-6 relative backdrop-filter backdrop-blur-sm transform transition duration-500 hover:scale-105">
                            <div className="absolute -top-3 -left-3 text-6xl text-yellow-400 opacity-50">"</div>
                            <p className="text-indigo-100 italic relative z-10 mt-4">J'utilise l'application tous les
                                jours pour mon temps de dévotion. Les quiz m'aident à mémoriser les Écritures et à
                                approfondir ma connaissance.</p>
                            <p className="mt-4 font-medium text-yellow-300">Thomas M., étudiant</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* CTA Section */}
            <div className="py-12 bg-gradient-to-r from-indigo-600 to-purple-700">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-3xl font-extrabold text-white sm:text-4xl mb-6">
                        Prêt à tester vos connaissances bibliques?
                    </h2>
                    <p className="max-w-2xl mx-auto text-xl text-indigo-100 mb-8">
                        Rejoignez des milliers d'utilisateurs qui approfondissent leur compréhension des Écritures de
                        façon ludique avec BibleQuiz.
                    </p>
                    {currentUser ? (
                        <Link
                            to="/dashboard"
                            className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-indigo-700 bg-yellow-400 hover:bg-yellow-500 transition transform hover:scale-105 duration-200 md:py-4 md:text-lg md:px-10"
                        >
                            Commencer maintenant
                        </Link>
                    ) : (
                        <Link
                            to="/signup"
                            className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-indigo-700 bg-yellow-400 hover:bg-yellow-500 transition transform hover:scale-105 duration-200 md:py-4 md:text-lg md:px-10"
                        >
                            Commencer maintenant
                        </Link>
                    )}
                </div>
            </div>

            {/* Bible Verse Section */}
            <div className="py-16 bg-indigo-800 bg-opacity-30">
                <div className="max-w-3xl mx-auto text-center px-4 sm:px-6 lg:px-8">
                    <blockquote className="mt-4">
                        <p className="text-xl font-medium text-white italic">
                            "Étudie et efforce-toi de te présenter devant Dieu comme un homme approuvé, un ouvrier qui
                            n'a pas à rougir et qui dispense avec droiture la parole de la vérité."
                        </p>
                        <footer className="mt-2">
                            <p className="text-base font-medium text-yellow-300">2 Timothée 2:15</p>
                        </footer>
                    </blockquote>
                </div>
            </div>

            {/* Footer */}
            <footer className="bg-indigo-950 bg-opacity-90">
                <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
                    <div className="md:flex md:items-center md:justify-between">
                        <div className="flex justify-center space-x-6 md:order-2">
                            {/* Icônes sociales */}
                            <a href={'#facebook'} className="text-indigo-200 hover:text-white">
                                <span className="sr-only">Facebook</span>
                                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                    <path fillRule="evenodd"
                                          d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
                                          clipRule="evenodd"/>
                                </svg>
                            </a>
                        </div>
                        <div className="mt-8 md:mt-0 md:order-1">
                            <div className="flex justify-center space-x-6 md:justify-start mb-4">
                                <a href={'#about'} className="text-sm text-indigo-200 hover:text-white">À propos</a>
                                <a href={'#confidentialites'}
                                   className="text-sm text-indigo-200 hover:text-white">Confidentialité</a>
                                <a href={'#conditions'} className="text-sm text-indigo-200 hover:text-white">Conditions
                                    d'utilisation</a>
                                <a href={'#contact'} className="text-sm text-indigo-200 hover:text-white">Contact</a>
                            </div>
                            <p className="text-center md:text-left text-base text-indigo-300">
                                &copy; {new Date().getFullYear()} BibleQuiz. Tous droits réservés - Propulsé par : <a
                                href={'wwww.yandoama.com'} target={'_blank'} rel={'noreferrer'}>Yandoama Consulting</a>
                            </p>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
